// Horizontal single-line carousel (v0.8.9).
//
// Two modes:
//   - default: scroll only when children overflow the host.
//     Avoids a duplicate copy when there's nothing to scroll
//     (the operator flagged that as confusing — "duplication of
//     bosses" on a small set).
//   - perView=N: force exactly N children visible at a time
//     regardless of viewport size. Clips the rest with overflow-
//     hidden and always renders a duplicate copy so the leftward
//     animation loops seamlessly back to the first child.
//     Use this when the consumer wants strict pagination-by-N
//     behaviour (e.g. /boss-fights wants 3 cards visible on a
//     4K wall display — without perView the cards just lay out
//     and 4.5 are visible).
//
// Pauses on hover, respects prefers-reduced-motion.

import {
  Children,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface Props {
  children: ReactNode;
  // Hold time in ms before each card starts sliding.
  holdMs?: number;
  // Time to take scrolling one card-width worth.
  scrollMs?: number;
  // Width of one "step" in px. If children are cards of ~N px
  // each, set this to roughly the card width. The default
  // measures the first child's width dynamically.
  stepPx?: number;
  // Force this many children visible at once. Host width is
  // pinned to (perView * step) + (perView - 1) * gap so the
  // host clips beyond N. When set, the duplicate copy is
  // always rendered and the loop animation always runs.
  perView?: number;
  className?: string;
}

const GAP_PX = 12; // Tailwind gap-3 = 0.75rem = 12px. Keep in sync.

export function Carousel({
  children,
  holdMs = 5000,
  scrollMs = 600,
  stepPx,
  perView,
  className = '',
}: Props) {
  const innerRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef<HTMLDivElement>(null);
  // Card width (px). Falls back to 16rem until the first card
  // measures itself. Used by the host sizing + the keyframe.
  const [step, setStep] = useState<number>(stepPx ?? 256);

  useLayoutEffect(() => {
    if (stepPx !== undefined) {
      setStep(stepPx);
      return;
    }
    // firstRef is a `display: contents` wrapper — it has no
    // bounding box. Measure the first DOM child inside it
    // instead.
    const wrapper = firstRef.current;
    if (!wrapper || !wrapper.firstElementChild) return;
    const w = (wrapper.firstElementChild as HTMLElement).getBoundingClientRect().width;
    if (w > 0) setStep(Math.round(w));
  }, [stepPx, children]);

  // Number of unique children. Single source of truth for the
  // loop length and the keyframe construction.
  const childCount = Children.count(children);

  // Decide whether to render the duplicate copy. In perView mode
  // the duplicate is always rendered because the loop wrap
  // relies on it. In default mode only when children overflow.
  const [overflowing, setOverflowing] = useState(false);
  useEffect(() => {
    if (perView !== undefined) return; // forced mode: always render duplicate
    const el = innerRef.current;
    if (!el) return;
    const check = () => setOverflowing(el.scrollWidth > el.clientWidth + 4);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children, step, perView]);

  // Build a keyframe that walks through every unique card once,
  // dwelling on each for holdMs then sliding it off in scrollMs.
  // Total cycle = childCount * (holdMs + scrollMs). At the end,
  // the duplicate's first card sits at translateX 0 (which is
  // where the original's first card was) — so when the animation
  // loops, no visible snap.
  //
  // Keyframe shape for N=3, with %s based on the full cycle:
  //   card 1: 0% .. (holdMs/total)%       at translateX(0)
  //            .. (holdMs+scrollMs)/total  at translateX(-stride)
  //   card 2: .. (holdMs+stride)/total    at translateX(-stride)
  //            .. (2*stride+scrollMs)/t   at translateX(-2*stride)
  //   card 3: .. (2*stride+holdMs)/t      at translateX(-2*stride)
  //            100%                        at translateX(-3*stride)
  // The duplicate copy picks up the slide at 100% and is at 0
  // visually when the loop restarts.
  const stride = step + GAP_PX;
  const totalMs = childCount * (holdMs + scrollMs);
  const keyframeSteps: string[] = [];
  for (let i = 0; i < childCount; i++) {
    const slideStart = (i * (holdMs + scrollMs) + holdMs) / totalMs * 100;
    const slideEnd = ((i + 1) * (holdMs + scrollMs)) / totalMs * 100;
    const holdStart = (i * (holdMs + scrollMs)) / totalMs * 100;
    keyframeSteps.push(
      `${holdStart.toFixed(3)}%, ${slideStart.toFixed(3)}% { transform: translateX(-${i * stride}px); }`,
      `${slideEnd.toFixed(3)}% { transform: translateX(-${(i + 1) * stride}px); }`,
    );
  }
  const css = `
    @keyframes carousel-walk {
      ${keyframeSteps.join('\n      ')}
    }
    .carousel-track {
      animation: carousel-walk ${totalMs}ms linear infinite;
      will-change: transform;
    }
    .carousel-track.no-loop {
      animation: none;
    }
    .carousel-host:hover .carousel-track { animation-play-state: paused; }
    /* Belt-and-suspenders: keep the scrollbar suppressed even
       when something inside forces overflow-x to a value the
       host can still scroll. PerView mode already uses
       overflow-x-hidden, but if a parent ever lets overflow
       escape we don't want a visible scrollbar. */
    .carousel-host {
      scrollbar-width: none;
      -ms-overflow-style: none;
    }
    .carousel-host::-webkit-scrollbar {
      display: none;
    }
    @media (prefers-reduced-motion: reduce) {
      .carousel-track {
        animation: none;
        transform: translateX(0);
      }
    }
  `;

  // When perView is set, the host uses a fixed width so the
  // children fit exactly and the track can be translated
  // predictably. Without perView the host is whatever the
  // parent gives it.
  //
  // The width formula adds EDGE_PADDING_PX on each side as a
  // safety buffer for sub-pixel rendering + the card's small
  // rotation. The host clips everything outside (overflow-x-
  // hidden) so the duplicate copy's right-side overflow doesn't
  // show a scrollbar.
  const EDGE_PADDING_PX = 16;
  const hostStyle = perView !== undefined
    ? { width: `${perView * step + (perView - 1) * GAP_PX + 2 * EDGE_PADDING_PX}px`, maxWidth: '100%' }
    : undefined;

  const showDuplicate = overflowing || perView !== undefined;
  // Only animate when there's actually something to scroll
  // through. With perView=N and childCount<=N, the cards fit
  // already — no need to scroll, and animating would just slide
  // visible content into empty space at the end of each cycle.
  const animate = showDuplicate && childCount > 0 && (perView === undefined || childCount > perView);

  return (
    <div
      className={`carousel-host relative ${perView !== undefined ? 'overflow-x-hidden overflow-y-hidden mx-auto' : 'overflow-x-auto overflow-y-hidden'} ${className}`}
      style={hostStyle}
    >
      <style>{css}</style>
      <div
        ref={innerRef}
        className={`carousel-track flex flex-nowrap gap-3 w-max ${animate ? '' : 'no-loop'}`}
      >
        {/* First copy */}
        <div ref={firstRef} className="contents">
          {children}
        </div>
        {/* Duplicate copy — rendered when children overflow OR
            perView is forced (the seamless loop needs somewhere
            to wrap to). */}
        {showDuplicate && (
          <div className="contents" aria-hidden>
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
